"use client";

import { clientEnv } from "@/clientEnv";
import { fireAndForget } from "@/lib/vercelUtils";
import { applyBootstrappedFlags } from "@/utils/posthog/bootstrapFlags";
import {
  CLIENT_INIT_CONFIG,
  getRenderedTheme,
  POSTHOG_BASE_PROPERTIES,
} from "@/utils/posthog/browserConfig";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect } from "react";

let initialized = false;

// Exported so surfaces that render outside `<AnalyticsProvider>` (e.g.
// `app/global-error.tsx`, which catches root-layout crashes) can init
// the singleton on demand. Idempotent.
export const initPosthog = () => {
  if (typeof window === "undefined") return;
  if (initialized) return;
  const token = clientEnv.NEXT_PUBLIC_POSTHOG_CAPTURE_KEY;
  if (!token) return;
  if (!initialized) {
    posthog.init(token, CLIENT_INIT_CONFIG);
    initialized = true;
    fireAndForget(applyBootstrappedFlags());
  }
  posthog.register(POSTHOG_BASE_PROPERTIES);
};

export const AnalyticsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  useEffect(() => {
    initPosthog();
    // A super-property, not an event, so `$pageview` and autocapture carry it.
    // Synchronous after init, so it lands before the initial `$pageview`, which
    // is scheduled on a timer.
    posthog.register({ theme: getRenderedTheme() });
    // Super-properties persist in browser storage, so without this a stale
    // value would follow the browser onto respondent routes and into later days.
    const observer = new MutationObserver(() =>
      posthog.register({ theme: getRenderedTheme() })
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
};
